/**
 * Toucan Reads — the handle on the open app. The store is the source of truth
 * for everything saved; a page's own unsaved state (what has been typed into
 * the Add field, whether the new-list dialog is open) belongs to that page's
 * component, or to the shell where more than one page reads it.
 */

import { orderedLists, required } from "toucan";
import type { ToucanApp } from "toucan";
import type { List, ReadsStore } from "./model.ts";

let current: ToucanApp<ReadsStore> | null = null;

/** Hand the pages the app `main()` has just opened. */
export function setApp(opened: ToucanApp<ReadsStore>): void {
  current = opened;
}

/**
 * Close the open store and forget it — what leaving the page would do, if a
 * page ever left. The suite does it between tests: an open store keeps
 * listeners on the window for coming back online and for the tab being looked
 * at again, and those outlive the tree that was rendered from it.
 */
export function closeApp(): void {
  current?.close();
  current = null;
}

/** The open app. Every caller runs after boot, so it is always there. */
export function app(): ToucanApp<ReadsStore> {
  return required(current, "app");
}

/** Shorthand for the synced store — by far the most-read thing on `app()`. */
export function store(): ReadsStore {
  return app().store;
}

/**
 * The lists in the order they are shown in. Every screen reads this rather
 * than `store().lists`, since the order is a key on each list. Mutations write
 * to `store().lists`.
 */
export function lists(): List[] {
  return orderedLists(store().lists);
}
